import React, { useState, useEffect, useMemo } from 'react';
import { 
  getArbicsxCycles, 
  createArbicsxCycle, 
  deleteArbicsxCycle, 
  startAwaitingPayment,
  claimPayment,
  burnAwaitingCycle,
  completeArbicsxCycle,
  getArbicsxSettings,
  updateArbicsxSettings,
  getArbicsxCycleDetails,
  getArbicsxCredits,
  updateArbicsxCredits,
  getMySubscription
} from '../../lib/api';
import axios from 'axios';

// Use relative URL for API calls to avoid CORS issues in production
const API = '';
import { useModule } from '../../contexts/ModuleContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatCurrencyColored, formatDate, getTodayString, getMonthName } from '../../lib/utils';
import { PropFirmLogo } from '../../lib/propFirmLogos';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Plus, Trash2, DollarSign, CheckCircle, Calendar, ChevronRight, ChevronDown, TrendingUp, Building2, Hash, Clock, CreditCard, Timer, Gift, AlertCircle, ExternalLink, History } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationControls } from '../../components/ui/pagination-controls';
import { IdentityAvatar } from './components';

export default function ArbicsxCicli() {
  const { impersonatedUser, isImpersonating } = useModule();
  const { isAdmin } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [credits, setCredits] = useState({ total_available: 0, has_credits: false });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [showNewCycleModal, setShowNewCycleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showBurnDateModal, setShowBurnDateModal] = useState(false);
  const [showClaimDateModal, setShowClaimDateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCycleDetails, setShowCycleDetails] = useState(false);
  const [cycleDetails, setCycleDetails] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  
  // Subscription state (for non-admin users)
  const [subscription, setSubscription] = useState(null);
  
  // Arbicsx accounts state (for multi-profile support)
  const [arbicsxAccounts, setArbicsxAccounts] = useState([]);
  
  // Slot history modal state
  const [showSlotHistory, setShowSlotHistory] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { slotNumber, accountId, accountLabel }
  const [slotHistoryCycles, setSlotHistoryCycles] = useState([]);
  const [loadingSlotHistory, setLoadingSlotHistory] = useState(false);
  
  // Modal modifica crediti
  const [showEditCreditsModal, setShowEditCreditsModal] = useState(false);
  const [editCreditsValue, setEditCreditsValue] = useState('');
  
  // Add new challenge cost inline
  const [showAddCostInput, setShowAddCostInput] = useState(false);
  const [newCostValue, setNewCostValue] = useState('');
  
  // New cycle form
  const [dateOption, setDateOption] = useState('today');
  const [formData, setFormData] = useState({
    start_date: getTodayString(),
    prop_firm: '',
    challenge_cost: '',
    identity: '',
    use_credit: false,
    credit_amount: '',  // Amount of credit to use (user chooses)
    num_accounts: 1,    // Number of accounts to create
    arbicsx_slot_key: '' // Slot key for sync (es. slot1_ciclo3)
  });
  
  // Complete form  
  const [completeData, setCompleteData] = useState({
    end_date: getTodayString(),
    net_profit: '',
    dateOption: 'today'
  });
  
  // Simple date form for burn/claim
  const [simpleDateForm, setSimpleDateForm] = useState({
    end_date: getTodayString(),
    dateOption: 'today'
  });
  
  // Modal scelta tipo riscossione (Profitto Prop)
  const [showPayoutTypeModal, setShowPayoutTypeModal] = useState(false);
  const [payoutTypeCycle, setPayoutTypeCycle] = useState(null);
  
  // Claim form con importo
  const [claimFormData, setClaimFormData] = useState({
    end_date: getTodayString(),
    dateOption: 'today',
    received_amount: ''
  });

  const impersonateUserId = isImpersonating ? impersonatedUser?.id : null;
  
  // Check if user can add new cycles (subscription not expired)
  // Admin and impersonating users are always allowed
  const canAddCycles = isAdmin || isImpersonating || !subscription || subscription.can_add_cycles !== false;
  const isExpired = subscription?.status === 'scaduto';
  
  // WhatsApp contact info
  const WHATSAPP_NUMBER = '+393488148192';
  const WHATSAPP_MESSAGE = 'Ciao Elia, vorrei rinnovare il mio abbonamento';
  const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cyclesRes, settingsRes, creditsRes] = await Promise.all([
        getArbicsxCycles(impersonateUserId, page, 100),
        getArbicsxSettings(impersonateUserId),
        getArbicsxCredits(impersonateUserId)
      ]);
      const cyclesData = cyclesRes.data;
      setCycles(cyclesData.items || []);
      setPagination({ total: cyclesData.total || 0, pages: cyclesData.pages || 1 });
      setSettings(settingsRes.data);
      setCredits(creditsRes.data);
      
      // Load Arbicsx accounts for current user (if admin with impersonation or user viewing own profile)
      if (impersonateUserId) {
        try {
          const accountsRes = await axios.get(`${API}/api/arbicsx/users/${impersonateUserId}/accounts`);
          setArbicsxAccounts(accountsRes.data.accounts || []);
        } catch (error) {
          console.error('Error loading Arbicsx accounts:', error);
          setArbicsxAccounts([]);
        }
      } else {
        setArbicsxAccounts([]);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch slot history - all cycles for a specific slot
  const fetchSlotHistory = async (slotNumber, accountId) => {
    setLoadingSlotHistory(true);
    try {
      const userId = isImpersonating ? impersonatedUser?.id : undefined;
      
      // Fetch all cycles (with max allowed page size)
      const response = await getArbicsxCycles(userId, 1, 200);
      const allCycles = response.data.items || [];
      
      // Filter cycles for this slot
      const slotCycles = allCycles.filter(c => {
        if (!c.arbicsx_slot_key) return false;
        const match = c.arbicsx_slot_key.match(/slot(\d+)/i);
        if (!match || parseInt(match[1]) !== slotNumber) return false;
        
        // For multi-account: filter by arbicsx_account_id
        if (arbicsxAccounts.length > 1 && accountId) {
          return c.arbicsx_account_id === accountId;
        }
        return true;
      });
      
      // Sort by date descending (most recent first)
      slotCycles.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      
      setSlotHistoryCycles(slotCycles);
    } catch (error) {
      console.error('Error loading slot history:', error);
      toast.error('Errore nel caricamento storico slot');
      setSlotHistoryCycles([]);
    } finally {
      setLoadingSlotHistory(false);
    }
  };
  
  // Open slot history modal
  const openSlotHistory = (slotNumber, accountId, accountLabel) => {
    setSelectedSlot({ slotNumber, accountId, accountLabel });
    setShowSlotHistory(true);
    fetchSlotHistory(slotNumber, accountId);
  };
  
  // Fetch subscription status for non-admin users
  useEffect(() => {
    if (isAdmin || isImpersonating) {
      setSubscription(null);
      return;
    }
    
    const fetchSubscription = async () => {
      try {
        const res = await getMySubscription();
        setSubscription(res.data);
      } catch (error) {
        // Silently fail - will treat as no restrictions
      }
    };
    
    fetchSubscription();
  }, [isAdmin, isImpersonating]);

  useEffect(() => {
    fetchData();
  }, [impersonateUserId, page]);

  // Auto-select single items when modal opens
  useEffect(() => {
    if (showNewCycleModal && settings) {
      const updates = {};
      
      // Auto-select identity if only one
      if (settings.identities?.length === 1 && !formData.identity) {
        updates.identity = settings.identities[0].name;
      }
      
      // Auto-select prop firm if only one
      if (settings.prop_firms?.length === 1 && !formData.prop_firm) {
        updates.prop_firm = settings.prop_firms[0].name;
      }
      
      // Auto-select challenge cost if only one
      if (settings.prop_costs_list?.length === 1 && !formData.challenge_cost) {
        updates.challenge_cost = settings.prop_costs_list[0].toString();
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [showNewCycleModal, settings]);

  // Filter cycles by status
  // Filter cycles by status and sort by date descending (newest first)
  const activeCycles = useMemo(() => 
    cycles.filter(c => c.status === 'active')
      .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')), 
    [cycles]
  );
  const awaitingCycles = useMemo(() => 
    cycles.filter(c => c.status === 'awaiting_payment')
      .sort((a, b) => (b.awaiting_payment_since || b.start_date || '').localeCompare(a.awaiting_payment_since || a.start_date || '')), 
    [cycles]
  );
  const targetPendingCycles = useMemo(() => 
    cycles.filter(c => c.status === 'target_pending')
      .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')), 
    [cycles]
  );
  const targetWalletCycles = useMemo(() => 
    cycles.filter(c => c.status === 'target_wallet')
      .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')), 
    [cycles]
  );
  const completedCycles = useMemo(() => cycles.filter(c => c.status === 'completed'), [cycles]);

  // Organize completed cycles by Year → Month (sorted newest first)
  const organizeCompletedCycles = () => {
    const organized = {};
    // Sort cycles by date descending first
    const sortedCycles = [...completedCycles].sort((a, b) => {
      const dateA = a.end_date || a.start_date || '';
      const dateB = b.end_date || b.start_date || '';
      return dateB.localeCompare(dateA);
    });
    
    sortedCycles.forEach(cycle => {
      const date = cycle.end_date || cycle.start_date;
      if (!date) return;
      const year = date.substring(0, 4);
      const month = date.substring(0, 7);
      
      if (!organized[year]) organized[year] = {};
      if (!organized[year][month]) organized[year][month] = [];
      organized[year][month].push(cycle);
    });
    return organized;
  };

  const organizedCycles = organizeCompletedCycles();
  const years = Object.keys(organizedCycles).sort((a, b) => b.localeCompare(a));

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`;
    setExpandedMonths(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openCycleDetails = async (cycle) => {
    try {
      const res = await getArbicsxCycleDetails(cycle.id, impersonateUserId);
      setCycleDetails(res.data);
      setShowCycleDetails(true);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
    }
  };

  const isFormValid = () => {
    // Basic validation
    if (!formData.prop_firm || !formData.identity) {
      return false;
    }
    
    // If using credit, must have credit_amount and challenge_cost
    if (formData.use_credit) {
      const creditAmount = parseFloat(formData.credit_amount) || 0;
      const challengeCost = parseFloat(formData.challenge_cost) || 0;
      // Must have a valid credit amount and a challenge cost selected
      return creditAmount > 0 && creditAmount <= credits.total_available && challengeCost > 0;
    }
    
    // If not using credit, must have challenge_cost
    return formData.challenge_cost && parseFloat(formData.challenge_cost) > 0;
  };

  const handleCreateCycle = async () => {
    if (!isFormValid()) {
      toast.error('Compila tutti i campi richiesti');
      return;
    }
    
    const numAccounts = parseInt(formData.num_accounts) || 1;
    const challengeCost = parseFloat(formData.challenge_cost);
    const propFirm = formData.prop_firm;
    
    try {
      const payload = {
        start_date: dateOption === 'today' ? getTodayString() : formData.start_date,
        prop_firm: propFirm,
        challenge_cost: challengeCost,
        identity: formData.identity,
        use_credit: formData.use_credit,
        credit_amount: formData.use_credit ? parseFloat(formData.credit_amount) : 0,
        num_accounts: numAccounts,
        arbicsx_slot_key: formData.arbicsx_slot_key || null
      };
      
      const response = await createArbicsxCycle(payload, impersonateUserId);
      const createdCount = response.data?.created_count || numAccounts;
      const totalCost = challengeCost * createdCount;
      
      if (createdCount > 1) {
        toast.success(`✅ Creati ${createdCount} conti ${propFirm} da ${formatCurrency(challengeCost)} ciascuno. Totale costi: ${formatCurrency(totalCost)}`);
      } else {
        toast.success('Conto creato con successo');
      }
      
      setShowNewCycleModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorMsg = Array.isArray(detail) 
        ? detail.map(d => d.msg || d.message).join(', ')
        : (typeof detail === 'string' ? detail : 'Errore nella creazione');
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setDateOption('today');
    setFormData({
      start_date: getTodayString(),
      prop_firm: '',
      challenge_cost: '',
      identity: '',
      use_credit: false,
      credit_amount: '',
      num_accounts: 1,
      arbicsx_slot_key: ''
    });
  };

  // Apre il modal per scegliere il tipo di riscossione
  const handleProfitProp = (cycle) => {
    setPayoutTypeCycle(cycle);
    setShowPayoutTypeModal(true);
  };
  
  // Conferma tipo riscossione e avvia countdown
  const handleConfirmPayoutType = async (payoutType) => {
    try {
      await startAwaitingPayment(payoutTypeCycle.id, { payout_type: payoutType }, impersonateUserId);
      const countdownText = payoutType === '48h' ? '48 ore' : '30 giorni';
      const profitNote = payoutType === '48h' ? ' (senza profitto 15%)' : ' (con profitto 15%)';
      toast.success(`Conto spostato in "In Attesa". Countdown ${countdownText} iniziato${profitNote}.`);
      setShowPayoutTypeModal(false);
      setPayoutTypeCycle(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handler for "Da Prelevare" - opens claim modal with amount input
  const handleClaimPayment = async () => {
    const received_amount = parseFloat(claimFormData.received_amount);
    if (isNaN(received_amount) || received_amount <= 0) {
      toast.error('Inserisci un importo rimborso valido (> 0)');
      return;
    }
    
    try {
      const end_date = claimFormData.dateOption === 'today' ? getTodayString() : claimFormData.end_date;
      const response = await claimPayment(selectedCycle.id, { 
        end_date, 
        received_amount 
      }, impersonateUserId);
      
      const payoutType = selectedCycle.payout_type || '30d';
      const profitMsg = payoutType === '30d' 
        ? ` Profitto 15%: ${formatCurrency(received_amount * 0.15)}` 
        : '';
      toast.success(`Pagamento riscosso! Rimborso: ${formatCurrency(received_amount)}.${profitMsg} Credito aggiunto.`);
      
      setShowClaimDateModal(false);
      setClaimFormData({ end_date: getTodayString(), dateOption: 'today', received_amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Modifica crediti manualmente
  const handleEditCredits = async () => {
    const newValue = parseFloat(editCreditsValue);
    if (isNaN(newValue) || newValue < 0) {
      toast.error('Inserisci un valore valido (>= 0)');
      return;
    }
    try {
      await updateArbicsxCredits({ total_available: newValue }, impersonateUserId);
      toast.success(`Crediti aggiornati a ${formatCurrency(newValue)}`);
      setShowEditCreditsModal(false);
      setEditCreditsValue('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  // Handler for adding new challenge cost inline
  const handleAddNewCost = async () => {
    const cost = parseFloat(newCostValue);
    if (isNaN(cost) || cost <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    // Check if cost already exists
    if (settings?.prop_costs_list?.includes(cost)) {
      toast.error('Questo importo esiste già');
      return;
    }
    try {
      const updatedCosts = [...(settings?.prop_costs_list || []), cost].sort((a, b) => a - b);
      await updateArbicsxSettings({ prop_costs_list: updatedCosts }, impersonateUserId);
      setSettings({ ...settings, prop_costs_list: updatedCosts });
      setNewCostValue('');
      setShowAddCostInput(false);
      // Auto-select the new cost
      setFormData({ ...formData, challenge_cost: cost.toString() });
      toast.success(`Costo $${cost} aggiunto`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // Handler for "Riscuoti (Anticipo)" - now with amount input
  const handleBurnAwaitingCycle = async () => {
    const received_amount = parseFloat(claimFormData.received_amount);
    if (isNaN(received_amount) || received_amount <= 0) {
      toast.error('Inserisci un importo rimborso valido (> 0)');
      return;
    }
    
    try {
      const end_date = claimFormData.dateOption === 'today' ? getTodayString() : claimFormData.end_date;
      const response = await burnAwaitingCycle(selectedCycle.id, { 
        end_date, 
        received_amount 
      }, impersonateUserId);
      
      const payoutType = selectedCycle.payout_type || '30d';
      const profitMsg = payoutType === '30d' 
        ? ` Profitto 15%: ${formatCurrency(received_amount * 0.15)}` 
        : ' (nessun profitto 15%)';
      toast.success(`Conto riscosso in anticipo! Rimborso: ${formatCurrency(received_amount)}.${profitMsg} Credito aggiunto.`);
      
      setShowBurnDateModal(false);
      setClaimFormData({ end_date: getTodayString(), dateOption: 'today', received_amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleCompleteCycle = async () => {
    if (!completeData.net_profit && completeData.net_profit !== 0) {
      toast.error('Inserisci il profitto broker');
      return;
    }
    try {
      const response = await completeArbicsxCycle(selectedCycle.id, {
        end_date: completeData.dateOption === 'today' ? getTodayString() : completeData.end_date,
        net_profit: parseFloat(completeData.net_profit)
      }, impersonateUserId);
      toast.success('Conto completato');
      
      setShowCompleteModal(false);
      setCompleteData({ end_date: getTodayString(), net_profit: '', dateOption: 'today' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel completamento');
    }
  };

  const confirmDeleteCycle = (cycleId) => {
    setDeleteId(cycleId);
    setShowDeleteDialog(true);
  };

  const handleDeleteCycle = async () => {
    try {
      await deleteArbicsxCycle(deleteId, impersonateUserId);
      toast.success('Conto eliminato');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Calculate days remaining for awaiting cycles
  const getDaysRemaining = (awaitingSince) => {
    if (!awaitingSince) return 30;
    const start = new Date(awaitingSince);
    const now = new Date();
    const diffMs = now - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - diffDays);
  };

  // Get identity info from settings
  const getIdentityInfo = (identityName) => {
    return settings?.identities?.find(i => i.name === identityName) || { name: identityName };
  };

  // Active Cycle Card Component
  const ActiveCycleCard = ({ cycle }) => {
    const identityInfo = getIdentityInfo(cycle.identity);
    
    return (
      <div className="ht-panel p-4 space-y-4" data-testid={`active-cycle-${cycle.id}`}>
        {/* Header with status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-[#3ddba9] rounded-full animate-pulse" />
            <span className="text-xs ht-pos font-medium">In corso</span>
          </div>
          <button
            onClick={() => confirmDeleteCycle(cycle.id)}
            className="text-gray-500 hover:text-red-400 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>

        {/* Prop Firm & Identity */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <PropFirmLogo firmName={cycle.prop_firm} size={32} />
            <span className="font-semibold text-white">{cycle.prop_firm}</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <IdentityAvatar identityName={cycle.identity} size={32} identities={settings?.identities} />
            <span className="text-sm text-gray-300">{cycle.identity || 'N/A'}</span>
          </div>
        </div>

        {/* Info Grid - Removed Payouts */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0d0d0d] rounded-lg px-3 py-2">
            <p className="text-xs text-disabled">Data Inizio</p>
            <p className="text-white text-sm">{formatDate(cycle.start_date)}</p>
          </div>
          <div className="bg-[#0d0d0d] rounded-lg px-3 py-2">
            <p className="text-xs text-disabled">Costo Challenge</p>
            {cycle.used_credit ? (
              <div className="flex items-center gap-1">
                <Gift size={12} className="ht-warn" />
                <span className="ht-warn text-sm">Credito usato (${cycle.used_credit.original_value})</span>
              </div>
            ) : (
              <p className="text-white font-mono text-sm">{formatCurrency(cycle.challenge_cost || 0)}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            onClick={() => handleProfitProp(cycle)}
            className="ht-btn ht-btn-primary flex-1"
            data-testid={`profit-prop-btn-${cycle.id}`}
          >
            <DollarSign size={14} className="mr-1" />
            Profitto Prop
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setSelectedCycle(cycle);
              setCompleteData({ end_date: getTodayString(), net_profit: '', dateOption: 'today' });
              setShowCompleteModal(true);
            }}
            className="ht-btn ht-btn-primary flex-1"
            data-testid={`complete-btn-${cycle.id}`}
          >
            <CheckCircle size={14} className="mr-1" />
            Completato
          </Button>
        </div>
      </div>
    );
  };

  // Awaiting Payment Card Component
  const AwaitingCycleCard = ({ cycle }) => {
    const identityInfo = getIdentityInfo(cycle.identity);
    const payoutType = cycle.payout_type || '30d';
    const countdownDays = payoutType === '48h' ? 2 : 30;
    
    // Calculate remaining time based on payout type
    const getTimeRemaining = () => {
      if (!cycle.awaiting_payment_since) return { days: countdownDays, hours: 0 };
      const startDate = new Date(cycle.awaiting_payment_since);
      const now = new Date();
      
      if (payoutType === '48h') {
        // For 48h, calculate in hours
        const hoursElapsed = Math.floor((now - startDate) / (1000 * 60 * 60));
        const hoursRemaining = 48 - hoursElapsed;
        return { 
          days: Math.floor(Math.max(0, hoursRemaining) / 24), 
          hours: Math.max(0, hoursRemaining) % 24,
          totalHours: Math.max(0, hoursRemaining)
        };
      } else {
        // For 30d, calculate in days
        const daysElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
        return { days: Math.max(0, 30 - daysElapsed), hours: 0, totalHours: 0 };
      }
    };
    
    const timeRemaining = getTimeRemaining();
    const isReadyToClaim = payoutType === '48h' 
      ? timeRemaining.totalHours <= 0 
      : timeRemaining.days <= 0;
    
    // For 48h type, no 15% bonus
    const showBonus = payoutType === '30d';

    return (
      <div className={`ht-panel border ${isReadyToClaim ? 'border-[rgba(61,219,169,.5)]' : 'border-[rgba(240,180,90,.5)]'} p-4 space-y-4`} data-testid={`awaiting-cycle-${cycle.id}`}>
        {/* Header with countdown and payout type badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Payout type badge */}
            <div className={`px-2 py-0.5 rounded text-xs font-medium ${
              payoutType === '48h' 
                ? 'bg-[rgba(40,44,52,.5)] text-[#8b919c]' 
                : 'bg-[rgba(61,219,169,.14)] ht-pos'
            }`}>
              {payoutType === '48h' ? '48h' : '30gg'}
            </div>
            
            {isReadyToClaim ? (
              <div className="flex items-center gap-1.5 bg-[rgba(61,219,169,.14)] px-2 py-1 rounded-full">
                <CheckCircle size={14} className="ht-pos" />
                <span className="text-xs ht-pos font-medium">Da Prelevare</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 bg-[rgba(240,180,90,.14)] px-2 py-1 rounded-full">
                <Timer size={14} className="ht-warn" />
                <span className="text-xs ht-warn font-medium">
                  {payoutType === '48h' 
                    ? (timeRemaining.totalHours > 24 
                        ? `${timeRemaining.days}g ${timeRemaining.hours}h` 
                        : `${timeRemaining.totalHours}h`)
                    : `tra ${timeRemaining.days} giorni`
                  }
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => confirmDeleteCycle(cycle.id)}
            className="text-gray-500 hover:text-red-400 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>

        {/* Prop Firm & Identity */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <PropFirmLogo firmName={cycle.prop_firm} size={32} />
            <span className="font-semibold text-white">{cycle.prop_firm}</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <IdentityAvatar identityName={cycle.identity} size={32} identities={settings?.identities} />
            <span className="text-sm text-gray-300">{cycle.identity || 'N/A'}</span>
          </div>
        </div>

        {/* Info - different for 48h vs 30d */}
        <div className={`grid ${showBonus ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
          <div className="bg-[#0d0d0d] rounded-lg px-3 py-2">
            <p className="text-xs text-disabled">Costo Challenge</p>
            <p className="text-white font-mono text-sm">{formatCurrency(cycle.challenge_cost || 0)}</p>
          </div>
          {showBonus && (
            <div className="bg-[#0d0d0d] rounded-lg px-3 py-2">
              <p className="text-xs text-disabled">Bonus 15%</p>
              <p className="ht-pos font-mono text-sm">Su importo ricevuto</p>
            </div>
          )}
        </div>
        
        {/* Note about payout type */}
        <div className="text-xs text-disabled text-center">
          {payoutType === '48h' 
            ? 'Riscossione senza profitto 15%' 
            : 'Riscossione con profitto 15% sul rimborso'
          }
        </div>

        {/* Claim Button - Only shown when ready */}
        {isReadyToClaim && (
          <Button
            onClick={() => {
              setSelectedCycle(cycle);
              setClaimFormData({ end_date: getTodayString(), dateOption: 'today', received_amount: '' });
              setShowClaimDateModal(true);
            }}
            className="ht-btn ht-btn-primary w-full"
            data-testid={`claim-btn-${cycle.id}`}
          >
            <DollarSign size={16} className="mr-2" />
            Riscuoti Pagamento
          </Button>
        )}

        {/* Riscuoti button - for early claim */}
        {!isReadyToClaim && (
          <Button
            size="sm"
            onClick={() => {
              setSelectedCycle(cycle);
              setClaimFormData({ end_date: getTodayString(), dateOption: 'today', received_amount: '' });
              setShowBurnDateModal(true);
            }}
            className="ht-btn ht-btn-warn w-full"
            data-testid={`riscuoti-btn-${cycle.id}`}
          >
            <DollarSign size={14} className="mr-1" />
            Riscuoti (Anticipo)
          </Button>
        )}
      </div>
    );
  };

  // Completed Cycle Card Component - EXPANDED with more details
  const CompletedCycleCard = ({ cycle, onClick }) => {
    const identityInfo = getIdentityInfo(cycle.identity);
    const netProfit = cycle.net_profit || 0;
    const refundAmount = settings?.prop_refund_amount || 3000;
    const bonus15 = refundAmount * 0.15;
    
    const getBorderColor = () => {
      if (netProfit > 0) return 'border-usdt/50';
      if (netProfit < 0) return 'border-red-500/50';
      return 'border-[#3c424c]/50';
    };

    const getCompletionTypeLabel = () => {
      switch(cycle.completion_type) {
        case 'prop_payout': return { text: 'Riscosso', color: 'ht-pos' };
        case 'prop_payout_30d': return { text: 'Riscosso (30gg)', color: 'ht-pos' };
        case 'prop_payout_48h': return { text: 'Riscosso (48h)', color: 'text-[#8b919c]' };
        case 'riscosso_anticipato': return { text: 'Riscosso (Anticipo)', color: 'ht-warn' };
        case 'riscosso_anticipato_30d': return { text: 'Riscosso (Anticipo 30gg)', color: 'ht-warn' };
        case 'riscosso_anticipato_48h': return { text: 'Riscosso (Anticipo 48h)', color: 'ht-warn' };
        case 'burn': return { text: 'Bruciato', color: 'text-red-400' };
        case 'broker_profit': return { text: 'Bruciato', color: 'text-red-400' };
        default: return { text: cycle.completion_type || 'Completato', color: 'text-gray-400' };
      }
    };

    const completionType = getCompletionTypeLabel();
    const showPropIncome = cycle.completion_type === 'prop_payout' || cycle.completion_type === 'riscosso_anticipato';
    
    // Determine if this cycle went through "Profitto Prop" flow (In Attesa section)
    const isProfittoProp = [
      'prop_payout', 'prop_payout_30d', 'prop_payout_48h',
      'riscosso_anticipato', 'riscosso_anticipato_30d', 'riscosso_anticipato_48h'
    ].includes(cycle.completion_type);

    return (
      <div 
        className={`ht-panel border ${getBorderColor()} p-4 space-y-3 cursor-pointer hover:bg-[#11161d] transition-colors`}
        onClick={onClick}
        data-testid={`completed-cycle-${cycle.id}`}
      >
        {/* Header: Prop Firm + Identity + Profitto Prop Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PropFirmLogo firmName={cycle.prop_firm} size={28} />
            <span className="font-semibold text-white">{cycle.prop_firm}</span>
            {isProfittoProp && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(61,219,169,.14)] ht-pos border border-[rgba(61,219,169,.4)]">
                Profitto Prop
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <IdentityAvatar identityName={cycle.identity} size={24} identities={settings?.identities} />
            <span className="text-sm text-gray-300">{cycle.identity || 'N/A'}</span>
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-center justify-end text-sm">
          <div className="text-disabled text-xs">
            {formatDate(cycle.start_date)} → {formatDate(cycle.end_date)}
          </div>
        </div>

        {/* Cost/Credit Used */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-disabled">Costo/Credito:</span>
          {cycle.used_credit ? (
            <span className="ht-warn flex items-center gap-1">
              <Gift size={12} />
              Credito ({formatCurrency(cycle.used_credit.original_value)})
            </span>
          ) : (
            <span className="text-white font-mono">{formatCurrency(cycle.challenge_cost || 0)}</span>
          )}
        </div>

        {/* Completion Type */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-disabled">Tipo Chiusura:</span>
          <span className={completionType.color}>{completionType.text}</span>
        </div>

        {/* Net Profit */}
        <div className="flex items-center justify-between">
          <span className="text-disabled text-sm">Profitto Netto:</span>
          <span className={`text-lg font-mono font-bold ${netProfit >= 0 ? 'ht-pos' : 'text-red-400'}`}>
            {formatCurrency(netProfit)}
          </span>
        </div>

        {/* Prop Income + 15% bonus (if applicable) */}
        {showPropIncome && (
          <div className="bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)] rounded-lg p-2 mt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Incasso Prop:</span>
              <span className="ht-pos font-mono">{formatCurrency(refundAmount)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-gray-400">Bonus 15%:</span>
              <span className="ht-pos font-mono">{formatCurrency(bonus15)}</span>
            </div>
          </div>
        )}

        {/* Credit Generated */}
        {cycle.credit_generated > 0 && (
          <div className="flex items-center justify-center gap-1 text-xs text-[#8b919c] rounded py-1">
            <Gift size={12} />
            Credito generato: <span className="ht-pos ht-num">{formatCurrency(cycle.credit_generated)}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading && cycles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  // Desktop View
  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">I Miei Conti</h1>
          <p className="ht-page-sub">
            Arbicsx - {isImpersonating ? `Dati di ${impersonatedUser?.name}` : 'Gestisci i tuoi conti'}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-1 w-full sm:w-auto">
          {canAddCycles ? (
            <button
              type="button"
              onClick={() => setShowNewCycleModal(true)}
              className="ht-btn ht-btn-primary justify-center"
              data-testid="new-cycle-btn"
            >
              <Plus size={14} />
              Nuovo Conto
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled
                className="ht-btn justify-center opacity-50 cursor-not-allowed"
                data-testid="new-cycle-btn-disabled"
              >
                <Plus size={14} />
                Nuovo Conto
              </button>
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs ht-alert flex items-center gap-1 justify-center sm:justify-end"
              >
                <AlertCircle size={12} />
                Abbonamento scaduto - Rinnova
                <ExternalLink size={10} />
              </a>
            </>
          )}
        </div>
      </div>

      {/* Expired Subscription Warning Banner (in-page) */}
      {isExpired && !isAdmin && !isImpersonating && (
        <div className="ht-panel border-[rgba(248,113,113,.45)] p-4 flex items-center justify-between flex-wrap gap-4" data-testid="expired-subscription-inline-warning">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={24} />
            <div>
              <p className="text-red-400 font-medium">Il tuo abbonamento è scaduto</p>
              <p className="text-red-400/70 text-sm">Puoi ancora consultare i tuoi dati, ma non puoi creare nuovi conti.</p>
            </div>
          </div>
          <a 
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
          >
            <ExternalLink size={16} />
            Rinnova su WhatsApp
          </a>
        </div>
      )}

      {/* I Miei Slot: quattro schede in riga (2x2 su telefono), senza riquadro esterno */}
      <div data-testid="my-slots-section">
        <h2 className="ht-panel-title text-sm mb-3">I Miei Slot</h2>
        
        {/* Render slots grouped by profile */}
        {(() => {
          // Helper function to render 4 slot cards
          const renderSlotCards = (accountId, accountLabel) => (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((slotNumber) => {
                const allActiveCycles = [...activeCycles, ...awaitingCycles, ...targetPendingCycles, ...targetWalletCycles];
                
                // Find cycles matching this slot number and account
                const slotCycles = allActiveCycles.filter(c => {
                  if (!c.arbicsx_slot_key) return false;
                  const match = c.arbicsx_slot_key.match(/slot(\d+)/i);
                  if (!match || parseInt(match[1]) !== slotNumber) return false;
                  // For multi-account: filter by arbicsx_account_id
                  if (arbicsxAccounts.length > 1) {
                    if (accountId) return c.arbicsx_account_id === accountId;
                    return !c.arbicsx_account_id; // Legacy cycles go to first/default
                  }
                  return true;
                });
                
                const cycle = slotCycles.length > 0 
                  ? slotCycles.sort((a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date))[0]
                  : null;
                
                const cycleNum = cycle?.arbicsx_slot_key?.match(/ciclo(\d+)/i)?.[1] || '1';
                
                const isAwaiting = cycle?.status === 'awaiting_payment';
                const isActive = cycle?.status === 'active';
                const isTargetPending = cycle?.status === 'target_pending';
                const isTargetWallet = cycle?.status === 'target_wallet';
                const hasOpenTrade = cycle?.has_open_trade;
                const isEmpty = !cycle;
                
                let remainingText = '';
                if (isAwaiting) {
                  const payoutType = cycle.payout_type || '48h';
                  const awaitingSince = cycle.awaiting_payment_since ? new Date(cycle.awaiting_payment_since) : new Date();
                  const totalHours = payoutType === '30d' ? 30 * 24 : 48;
                  const endDate = new Date(awaitingSince);
                  endDate.setHours(endDate.getHours() + totalHours);
                  const remainingMs = endDate - new Date();
                  const remainingHours = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60)));
                  const remainingDays = Math.floor(remainingHours / 24);
                  remainingText = remainingDays > 0 ? `${remainingDays}g ${remainingHours % 24}h` : `${remainingHours}h`;
                }
                
                let cardClasses = '', indicatorColor = '', statusText = '', statusBgClass = '';
                if (isEmpty) { cardClasses = 'bg-[#0d1117] border-[#282c34]'; indicatorColor = 'bg-[#3c424c]'; }
                else if (isTargetPending) { cardClasses = 'bg-[#0d1117] border-[rgba(240,180,90,.45)]'; indicatorColor = 'bg-[#f0b45a]'; statusText = 'TARGET IN ELABORAZIONE'; statusBgClass = 'ht-tag ht-tag-warn'; }
                else if (isTargetWallet) { cardClasses = 'bg-[#0d1117] border-[rgba(240,180,90,.45)]'; indicatorColor = 'bg-[#f0b45a]'; statusText = 'TARGET WALLET'; statusBgClass = 'ht-tag ht-tag-warn'; }
                else if (isAwaiting) { cardClasses = 'bg-[#0d1117] border-[rgba(240,180,90,.45)]'; indicatorColor = 'bg-[#f0b45a]'; statusText = 'IN ATTESA'; statusBgClass = 'ht-tag ht-tag-warn'; }
                else if (hasOpenTrade) { cardClasses = 'bg-[#0d1117] border-[rgba(240,180,90,.45)]'; indicatorColor = 'bg-[#f0b45a]'; statusText = 'TRADING'; statusBgClass = 'ht-tag ht-tag-warn'; }
                else if (isActive) { cardClasses = 'bg-[#0d1117] border-[#282c34]'; indicatorColor = 'bg-[#3ddba9]'; statusText = 'ATTIVO'; statusBgClass = 'ht-tag ht-tag-pos'; }
                
                return (
                  <div 
                    key={`${accountId || 'default'}-slot-${slotNumber}`} 
                    data-testid={`slot-card-${slotNumber}`} 
                    className={`relative rounded-[14px] border p-3 sm:p-4 flex flex-col min-h-[152px] transition-colors cursor-pointer ${cardClasses}`}
                    onClick={() => openSlotHistory(slotNumber, accountId, accountLabel)}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${indicatorColor}`}></div>
                        <span className="text-xs text-[#8b919c]">Slot {slotNumber}</span>
                      </div>
                      {statusText && <span className={`${statusBgClass} text-[9px] sm:text-[10px]`}>{statusText}</span>}
                    </div>
                    {isEmpty ? (
                      <div className="flex-1 flex items-center justify-center"><span className="text-[#5f6672] text-xs">VUOTO</span></div>
                    ) : (
                      <div className="flex-1 flex flex-col">
                        <p className="text-xl font-semibold text-white font-heading leading-tight">Ciclo {cycleNum}</p>
                        <p className="text-xs text-[#8b919c] truncate">{cycle.prop_firm}</p>
                        {isTargetPending ? (
                          <div className="mt-auto pt-2 space-y-1">
                            {cycle.payout_type ? (
                              <div className="flex items-center gap-1.5 text-xs text-[#8b919c]"><Clock size={11} className="flex-shrink-0" /><span>Rimborso: {cycle.payout_type === '30d' ? '30 giorni' : '48 ore'}</span></div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-xs ht-warn"><AlertCircle size={11} className="flex-shrink-0" /><span>Tipo rimborso in definizione</span></div>
                            )}
                            <div className="flex items-center gap-1.5 text-xs text-[#8b919c]"><Calendar size={11} className="flex-shrink-0" /><span className="ht-num">{formatDate(cycle.start_date)}</span></div>
                          </div>
                        ) : isTargetWallet ? (
                          <div className="mt-auto pt-2 space-y-1">
                            <div className="flex items-center gap-1.5 text-xs text-[#8b919c]"><Clock size={11} className="flex-shrink-0" /><span>Rimborso: {cycle.payout_type === '30d' ? '30 giorni' : '48 ore'}</span></div>
                            <div className="flex items-center gap-1.5 text-xs ht-warn"><CreditCard size={11} className="flex-shrink-0" /><span>Inserire wallet su Arbicsx</span></div>
                          </div>
                        ) : isAwaiting ? (
                          <div className="mt-auto pt-2 space-y-1">
                            <div className="flex items-center gap-1.5 text-xs text-[#8b919c]"><Clock size={11} className="flex-shrink-0" /><span>Rimborso: {cycle.payout_type === '30d' ? '30 giorni' : '48 ore'}</span></div>
                            <div className="flex items-center gap-1.5 text-xs ht-warn ht-num"><Timer size={11} className="flex-shrink-0" /><span>{remainingText} rimanenti</span></div>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedCycle(cycle); setClaimFormData({ end_date: getTodayString(), dateOption: 'today', received_amount: '' }); setShowClaimDateModal(true); }} className="ht-btn ht-btn-warn w-full justify-center mt-2" data-testid={`slot-riscuoti-btn-${slotNumber}`}><DollarSign size={12} />Riscuoti</button>
                          </div>
                        ) : (
                          <div className="mt-auto pt-2 flex items-center gap-1.5 text-xs text-[#8b919c]"><Calendar size={11} className="flex-shrink-0" /><span className="ht-num">{formatDate(cycle.start_date)}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
          
          // Single or no profile: show without header
          if (arbicsxAccounts.length <= 1) {
            return renderSlotCards(arbicsxAccounts[0]?.id || null, null);
          }
          
          // Multiple profiles: show with headers
          return (
            <div className="space-y-5">
              {arbicsxAccounts.map((account) => (
                <div key={account.id}>
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#282c34]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-white truncate">{account.label}</span>
                      <span className="ht-tag">{account.ready_accounts} conti pronti</span>
                    </div>
                  </div>
                  {renderSlotCards(account.id, account.label)}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="completed" className="w-full">
        <TabsList className="ht-tabs bg-transparent border-0 p-0 h-auto">
          <TabsTrigger 
            value="completed"
            className="ht-btn"
          >
            <CheckCircle size={13} />
            Completati ({completedCycles.length})
          </TabsTrigger>
        </TabsList>

        {/* Completed Cycles Tab */}
        <TabsContent value="completed" className="mt-4">
          {completedCycles.length === 0 ? (
            <div className="ht-panel ht-empty">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Nessun conto completato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {years.map(year => {
                const yearData = organizedCycles[year];
                const months = Object.keys(yearData).sort((a, b) => b.localeCompare(a));
                const totalCycles = months.reduce((sum, month) => sum + yearData[month].length, 0);
                
                return (
                  <div key={year} className="ht-acc">
                    <button
                      onClick={() => toggleYear(year)}
                      className="ht-acc-head"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-4 h-4 ht-pos" />
                        <span className="ht-acc-title">{year}</span>
                        <span className="text-xs text-[#5f6672]">({totalCycles} conti)</span>
                      </span>
                      {expandedYears[year] ? <ChevronDown className="w-4 h-4 ht-chev" /> : <ChevronRight className="w-4 h-4 ht-chev" />}
                    </button>
                    
                    {expandedYears[year] && (
                      <div className="border-t border-[#282c34]">
                        {months.map(month => {
                          const monthKey = `${year}-${month}`;
                          const monthCycles = yearData[month];
                          
                          return (
                            <div key={month} className="border-b last:border-b-0 border-[#1e2127]">
                              <button
                                onClick={() => toggleMonth(year, month)}
                                className="w-full flex items-center justify-between p-3 pl-8 hover:bg-[rgba(40,44,52,.3)] transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#3ddba9]" />
                                  <span className="text-sm text-[#e6e9ee]">{getMonthName(month)}</span>
                                  <span className="text-xs text-[#5f6672]">({monthCycles.length})</span>
                                </div>
                                {expandedMonths[monthKey] ? <ChevronDown className="w-4 h-4 ht-chev" /> : <ChevronRight className="w-4 h-4 ht-chev" />}
                              </button>
                              
                              {expandedMonths[monthKey] && (
                                <div className="p-3 pl-8 bg-[#0d0d0d]/50 grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {monthCycles.map(cycle => (
                                    <CompletedCycleCard 
                                      key={cycle.id} 
                                      cycle={cycle}
                                      onClick={() => openCycleDetails(cycle)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <PaginationControls
          currentPage={page}
          totalPages={pagination.pages}
          onPageChange={setPage}
        />
      )}

      {/* New Cycle Modal */}
      <Dialog open={showNewCycleModal} onOpenChange={setShowNewCycleModal}>
        <DialogContent className="ht-panel text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo Conto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Data */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Data</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDateOption('today')}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    dateOption === 'today' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400 hover:border-[#3c424c]'
                  }`}
                >
                  <Calendar size={16} className="inline mr-2" />
                  Oggi
                </button>
                <button
                  type="button"
                  onClick={() => setDateOption('custom')}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    dateOption === 'custom' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400 hover:border-[#3c424c]'
                  }`}
                >
                  Seleziona data
                </button>
              </div>
              {dateOption === 'custom' && (
                <Input
                  type="date"
                  value={formData.start_date}
                  max={getTodayString()}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-[#0d0d0d] border-[#282c34] text-white mt-2"
                />
              )}
            </div>

            {/* Identità */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Identità</Label>
              <div className="flex flex-wrap gap-2">
                {settings?.identities?.length > 0 ? settings.identities.map((identity) => (
                  <button
                    key={identity.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, identity: identity.name })}
                    className={`py-2 px-4 rounded-lg border transition-all flex items-center gap-2 ${
                      formData.identity === identity.name 
                        ? 'ht-tab-on' 
                        : 'bg-[#0d0d0d] border-[#282c34] text-gray-400 hover:border-[#3c424c]'
                    }`}
                  >
                    <Avatar className="w-6 h-6">
                      {identity.photo_url && <AvatarImage src={identity.photo_url} />}
                      <AvatarFallback className="bg-gray-600 text-xs">{identity.name[0]}</AvatarFallback>
                    </Avatar>
                    {identity.name}
                  </button>
                )) : (
                  <p className="text-sm text-disabled">Configura le Identità nelle Impostazioni</p>
                )}
              </div>
            </div>

            {/* Prop Firm */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Prop Firm</Label>
              <div className="flex flex-wrap gap-2">
                {settings?.prop_firms?.length > 0 ? settings.prop_firms.map((firm) => (
                  <button
                    key={firm.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, prop_firm: firm.name })}
                    className={`py-2 px-4 rounded-lg border transition-all flex items-center gap-2 ${
                      formData.prop_firm === firm.name 
                        ? 'ht-tab-on' 
                        : 'bg-[#0d0d0d] border-[#282c34] text-gray-400 hover:border-[#3c424c]'
                    }`}
                  >
                    <PropFirmLogo firmName={firm.name} size={20} />
                    {firm.name}
                  </button>
                )) : (
                  <p className="text-sm text-disabled">Configura le Prop Firm nelle Impostazioni</p>
                )}
              </div>
            </div>

            {/* Costo Challenge */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Costo Challenge</Label>
              <div className="flex flex-wrap gap-2 items-center">
                {/* Normal cost options */}
                {settings?.prop_costs_list?.length > 0 ? settings.prop_costs_list.map((cost) => (
                  <button
                    key={cost}
                    type="button"
                    onClick={() => setFormData({ ...formData, challenge_cost: cost.toString() })}
                    className={`py-2 px-4 rounded-lg border transition-all font-mono ${
                      formData.challenge_cost === cost.toString() 
                        ? 'ht-tab-on' 
                        : 'bg-[#0d0d0d] border-[#282c34] text-gray-400 hover:border-[#3c424c]'
                    }`}
                  >
                    ${cost}
                  </button>
                )) : null}
                
                {/* Add new cost inline */}
                {showAddCostInput ? (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input
                      type="number"
                      min="1"
                      value={newCostValue}
                      onChange={(e) => setNewCostValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewCost();
                        } else if (e.key === 'Escape') {
                          setShowAddCostInput(false);
                          setNewCostValue('');
                        }
                      }}
                      placeholder="0"
                      className="w-20 h-9 bg-[#0d0d0d] border-[#282c34] text-white font-mono text-center"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCost}
                      className="ht-btn ht-btn-icon ht-btn-primary"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddCostInput(false); setNewCostValue(''); }}
                      className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAddCostInput(true)}
                    className="py-2 px-3 rounded-lg border border-dashed border-gray-600 text-gray-500 hover:border-gray-400 hover:text-gray-300 transition-all"
                    title="Aggiungi nuovo importo"
                  >
                    <Plus size={16} />
                  </button>
                )}
                
                {!settings?.prop_costs_list?.length && !showAddCostInput && (
                  <p className="text-sm text-disabled">Clicca + per aggiungere un costo</p>
                )}
              </div>
            </div>

            {/* Usa Credito - Only show if credits available AND challenge cost selected */}
            {credits.total_available > 0 && formData.challenge_cost && (
              <div className="space-y-3 bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)] rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="use_credit"
                    checked={formData.use_credit}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      use_credit: e.target.checked,
                      credit_amount: e.target.checked ? '' : ''
                    })}
                    className="w-4 h-4 rounded border-[#f0b45a] accent-[#f0b45a] focus:ring-[#f0b45a]"
                  />
                  <Label htmlFor="use_credit" className="ht-warn cursor-pointer flex items-center gap-2">
                    <Gift size={16} />
                    Usa Credito ({formatCurrency(credits.total_available)} disponibili)
                  </Label>
                </div>
                
                {formData.use_credit && (
                  <div className="space-y-3">
                    <Label className="text-xs text-[#8b919c]">Importo credito da scalare ($)</Label>
                    {(() => {
                      const challengeCost = parseFloat(formData.challenge_cost) || 0;
                      const totalCost = challengeCost * (formData.num_accounts || 1);
                      const maxCredit = Math.min(credits.total_available, totalCost);
                      const maxAccountsCoverable = Math.floor(credits.total_available / challengeCost);
                      
                      return (
                        <>
                          {/* Quick selection buttons */}
                          {challengeCost > 0 && maxAccountsCoverable >= 1 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {[1, 2, 3, 4].map(num => {
                                const creditForAccounts = challengeCost * num;
                                const canAfford = credits.total_available >= creditForAccounts && num <= (formData.num_accounts || 1);
                                const isSelected = parseFloat(formData.credit_amount) === creditForAccounts;
                                
                                if (!canAfford) return null;
                                
                                return (
                                  <button
                                    key={num}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, credit_amount: creditForAccounts.toString() })}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                      isSelected 
                                        ? 'ht-tab-on' 
                                        : 'bg-[#0d1117] text-[#e6e9ee] border border-[#282c34] hover:border-[rgba(240,180,90,.5)]'
                                    }`}
                                  >
                                    {num} {num === 1 ? 'conto' : 'conti'}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          
                          <Input
                            type="number"
                            min="1"
                            max={maxCredit}
                            placeholder={`Max: ${formatCurrency(maxCredit)}`}
                            value={formData.credit_amount}
                            onChange={(e) => setFormData({ ...formData, credit_amount: e.target.value })}
                            className="bg-[#0d0d0d] border-[#282c34] text-white font-mono"
                          />
                          {formData.credit_amount && parseFloat(formData.credit_amount) > 0 && (
                            <div className="text-xs space-y-1">
                              <p className="ht-warn">
                                Credito usato: {formatCurrency(parseFloat(formData.credit_amount))}
                              </p>
                              <p className="text-gray-400">
                                Costo totale: {formatCurrency(totalCost)} → Costo effettivo: {formatCurrency(totalCost - parseFloat(formData.credit_amount))}
                              </p>
                              <p className="text-disabled">
                                Crediti rimanenti: {formatCurrency(credits.total_available - parseFloat(formData.credit_amount))}
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            
            {/* Numero di conti */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Numero di conti da aprire</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.num_accounts}
                  onChange={(e) => setFormData({ ...formData, num_accounts: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-24 bg-[#0d0d0d] border-[#282c34] text-white font-mono text-center"
                />
                {formData.num_accounts > 1 && formData.challenge_cost && (
                  <div className="text-xs text-[#8b919c]">
                    = <span className="text-white font-mono">{formatCurrency(parseFloat(formData.challenge_cost) * formData.num_accounts)}</span> totale
                  </div>
                )}
              </div>
              {formData.num_accounts > 1 && (
                <p className="text-xs text-disabled">
                  Verranno creati {formData.num_accounts} conti identici con gli stessi parametri
                </p>
              )}
            </div>

            {/* Slot Key (opzionale) */}
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Slot Key <span className="text-disabled">(opzionale)</span></Label>
              <Input
                type="text"
                placeholder="es. slot1_ciclo3"
                value={formData.arbicsx_slot_key}
                onChange={(e) => setFormData({ ...formData, arbicsx_slot_key: e.target.value })}
                className="bg-[#0d0d0d] border-[#282c34] text-white font-mono"
              />
              <p className="text-xs text-disabled">
                Formato: slot{'{N}'}_ciclo{'{N}'} — usato per la sincronizzazione automatica
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewCycleModal(false); resetForm(); }} className="border-[#282c34]">
              Annulla
            </Button>
            <Button 
              onClick={handleCreateCycle} 
              disabled={!isFormValid()}
              className="ht-btn ht-btn-primary"
            >
              Crea Conto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Completa Conto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-[#0d0d0d] rounded-lg p-3">
              <p className="text-xs text-disabled">Conto</p>
              <div className="flex items-center gap-2 mt-1">
                <PropFirmLogo firmName={selectedCycle?.prop_firm} size={20} />
                <span className="text-white">{selectedCycle?.prop_firm}</span>
              </div>
            </div>

            {/* Profit Input */}
            <div className="space-y-2">
              <Label className="text-gray-300">Profitto Netto Broker ($)</Label>
              <Input
                type="number"
                value={completeData.net_profit}
                onChange={(e) => setCompleteData({ ...completeData, net_profit: e.target.value })}
                className="bg-[#0d0d0d] border-[#282c34] text-white"
                placeholder="Es: 500 (o -200 se in perdita)"
              />
              <p className="text-xs text-disabled">
                Inserisci il profitto generato dal broker. Può essere negativo.
              </p>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">Data</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCompleteData({ ...completeData, dateOption: 'today' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    completeData.dateOption === 'today' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Oggi
                </button>
                <button
                  type="button"
                  onClick={() => setCompleteData({ ...completeData, dateOption: 'custom' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    completeData.dateOption === 'custom' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Seleziona
                </button>
              </div>
              {completeData.dateOption === 'custom' && (
                <Input
                  type="date"
                  value={completeData.end_date}
                  onChange={(e) => setCompleteData({ ...completeData, end_date: e.target.value })}
                  className="bg-[#0d0d0d] border-[#282c34] text-white mt-2"
                />
              )}
            </div>

            {/* Info about what will happen */}
            {completeData.net_profit && (
              <div className={`rounded-lg p-3 ${parseFloat(completeData.net_profit) >= 0 ? 'bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)]' : 'bg-red-500/10 border border-red-500/30'}`}>
                <p className={`text-sm ${parseFloat(completeData.net_profit) >= 0 ? 'ht-pos' : 'text-red-400'}`}>
                  {parseFloat(completeData.net_profit) >= 0 ? (
                    <>
                      Profitto: <strong>{formatCurrency(parseFloat(completeData.net_profit))}</strong> → Profitti<br/>
                      {selectedCycle?.used_credit ? (
                        <>Credito originale: <strong>{formatCurrency(selectedCycle.used_credit.original_value)}</strong> → Costi Prop</>
                      ) : (
                        <>Costo Challenge: <strong>{formatCurrency(selectedCycle?.challenge_cost || 0)}</strong> → Costi Prop</>
                      )}
                    </>
                  ) : (
                    <>
                      Perdita: <strong>{formatCurrency(parseFloat(completeData.net_profit))}</strong> → Profitti<br/>
                      <span className="text-gray-400">Nessun costo registrato (profitto negativo)</span>
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteModal(false)} className="border-[#282c34]">
              Annulla
            </Button>
            <Button onClick={handleCompleteCycle} className="ht-btn ht-btn-primary">
              Completa Conto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cycle Details Modal */}
      <Dialog open={showCycleDetails} onOpenChange={setShowCycleDetails}>
        <DialogContent className="ht-panel text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              <PropFirmLogo firmName={cycleDetails?.cycle?.prop_firm} size={28} />
              Dettagli Ciclo - {cycleDetails?.cycle?.prop_firm}
            </DialogTitle>
          </DialogHeader>
          {cycleDetails && (
            <div className="space-y-6 py-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4 bg-[#0d0d0d] rounded-lg p-4">
                <div>
                  <p className="text-xs text-disabled">Identità</p>
                  <p className="text-white">{cycleDetails.cycle.identity || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Data Inizio</p>
                  <p className="text-white">{formatDate(cycleDetails.cycle.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Data Fine</p>
                  <p className="text-white">{formatDate(cycleDetails.cycle.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Costo Challenge</p>
                  <p className="text-white font-mono">{formatCurrency(cycleDetails.cycle.challenge_cost || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Tipo Completamento</p>
                  <p className="text-white">{cycleDetails.cycle.completion_type || 'N/A'}</p>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-r from-accent-primary-light to-transparent border accent-border-light rounded-lg p-4">
                <h4 className="text-sm font-semibold accent-text-primary mb-3">Riepilogo</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-disabled">Profitto Netto</p>
                    <p className={`text-2xl font-mono font-bold ${(cycleDetails.cycle.net_profit || 0) >= 0 ? 'ht-pos' : 'text-red-400'}`}>
                      {formatCurrency(cycleDetails.cycle.net_profit || 0)}
                    </p>
                  </div>
                  {cycleDetails.cycle.credit_generated_id && (
                    <div>
                      <p className="text-xs text-disabled">Credito Generato</p>
                      <p className="ht-warn flex items-center gap-1">
                        <Gift size={16} />
                        {formatCurrency(cycleDetails.cycle.challenge_cost || 0)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => {
                confirmDeleteCycle(cycleDetails?.cycle?.id);
                setShowCycleDetails(false);
              }}
              className="ht-btn ht-btn-danger"
            >
              <Trash2 size={16} className="mr-2" />
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Sei sicuro di voler eliminare questo conto? Verranno eliminati anche tutti i profitti, incassi e costi associati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCycle} className="ht-btn ht-btn-danger">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Burn Awaiting Modal - Now with amount input */}
      <Dialog open={showBurnDateModal} onOpenChange={setShowBurnDateModal}>
        <DialogContent className="ht-panel text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DollarSign className="ht-warn" size={20} />
              Riscuoti (Anticipo)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Tipo riscossione badge */}
            <div className="flex items-center gap-2 justify-center">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                selectedCycle?.payout_type === '48h' 
                  ? 'bg-[rgba(40,44,52,.5)] text-[#8b919c]' 
                  : 'bg-[rgba(61,219,169,.14)] ht-pos'
              }`}>
                {selectedCycle?.payout_type === '48h' ? 'Riscossione 48h (Anticipo)' : 'Riscossione 30gg (Anticipo)'}
              </div>
            </div>
            
            {/* Campo importo rimborso ricevuto */}
            <div className="space-y-2">
              <Label className="text-gray-300">Importo Rimborso Ricevuto ($)</Label>
              <Input
                type="number"
                placeholder="Es: 3600"
                value={claimFormData.received_amount}
                onChange={(e) => setClaimFormData({ ...claimFormData, received_amount: e.target.value })}
                className="bg-[#0d0d0d] border-[#282c34] text-white font-mono text-lg"
              />
              <p className="text-xs text-disabled">Inserisci l'importo effettivo ricevuto dalla prop firm</p>
            </div>
            
            {/* Preview calcoli */}
            {claimFormData.received_amount && parseFloat(claimFormData.received_amount) > 0 && (
              <div className="bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)] rounded-lg p-3 space-y-2">
                <p className="text-xs ht-warn">Verrà registrato:</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Rimborso</span>
                    <span className="ht-warn font-mono">{formatCurrency(parseFloat(claimFormData.received_amount))}</span>
                  </div>
                  {selectedCycle?.payout_type !== '48h' && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Profitto (15%)</span>
                      <span className="ht-pos font-mono">{formatCurrency(parseFloat(claimFormData.received_amount) * 0.15)}</span>
                    </div>
                  )}
                  {selectedCycle?.payout_type === '48h' && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Profitto</span>
                      <span className="text-disabled font-mono">Nessuno (48h)</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-[#282c34]">
                    <span className="text-[#8b919c] flex items-center gap-1">
                      <Gift size={14} />
                      Credito generato
                    </span>
                    <span className="ht-pos font-mono">{formatCurrency(selectedCycle?.challenge_cost || 0)}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-300">Data</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setClaimFormData({ ...claimFormData, dateOption: 'today' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    claimFormData.dateOption === 'today' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Oggi
                </button>
                <button
                  type="button"
                  onClick={() => setClaimFormData({ ...claimFormData, dateOption: 'custom' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    claimFormData.dateOption === 'custom' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Seleziona
                </button>
              </div>
              {claimFormData.dateOption === 'custom' && (
                <Input
                  type="date"
                  value={claimFormData.end_date}
                  onChange={(e) => setClaimFormData({ ...claimFormData, end_date: e.target.value })}
                  className="bg-[#0d0d0d] border-[#282c34] text-white mt-2"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBurnDateModal(false)} className="border-[#282c34]">
              Annulla
            </Button>
            <Button 
              onClick={handleBurnAwaitingCycle} 
              className="ht-btn ht-btn-warn"
              disabled={!claimFormData.received_amount || parseFloat(claimFormData.received_amount) <= 0}
            >
              <DollarSign size={16} className="mr-2" />
              Riscuoti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Payment Modal - Simple date selection only */}
      {/* Modal scelta tipo riscossione */}
      <Dialog open={showPayoutTypeModal} onOpenChange={setShowPayoutTypeModal}>
        <DialogContent className="ht-panel text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DollarSign className="ht-pos" size={20} />
              Tipo di Riscossione
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-xs text-[#8b919c]">
              Scegli il tipo di riscossione per il conto <span className="text-white font-semibold">{payoutTypeCycle?.prop_firm}</span>
            </p>
            
            {/* Opzione A - 48h */}
            <button
              onClick={() => handleConfirmPayoutType('48h')}
              className="w-full bg-[#0d0d0d] hover:bg-[#1a1a2e] border border-blue-500/50 hover:border-blue-400 rounded-xl p-4 text-left transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-[rgba(40,44,52,.5)] px-2 py-0.5 rounded text-xs font-bold text-[#8b919c]">48h</div>
                  <span className="font-semibold text-white group-hover:text-[#8b919c] transition-colors">Riscossione Rapida</span>
                </div>
                <Clock size={20} className="text-[#8b919c]" />
              </div>
              <ul className="text-xs text-gray-400 space-y-1 ml-1">
                <li>• Countdown di 48 ore</li>
                <li>• Importo rimborso variabile</li>
                <li>• Costo challenge → Credito</li>
                <li className="text-red-400">• Nessun profitto 15%</li>
              </ul>
            </button>
            
            {/* Opzione B - 30gg */}
            <button
              onClick={() => handleConfirmPayoutType('30d')}
              className="w-full bg-[#0d0d0d] hover:bg-[#1a1a2e] border border-usdt/50 hover:border-usdt rounded-xl p-4 text-left transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-[rgba(61,219,169,.14)] px-2 py-0.5 rounded text-xs font-bold ht-pos">30gg</div>
                  <span className="font-semibold text-white group-hover:ht-pos transition-colors">Riscossione Standard</span>
                </div>
                <Calendar size={20} className="ht-pos" />
              </div>
              <ul className="text-xs text-gray-400 space-y-1 ml-1">
                <li>• Countdown di 30 giorni</li>
                <li>• Importo rimborso variabile</li>
                <li>• Costo challenge → Credito</li>
                <li className="ht-pos">• Profitto 15% sul rimborso ricevuto</li>
              </ul>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutTypeModal(false)} className="border-[#282c34] w-full">
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Date Modal - ora con campo importo */}
      <Dialog open={showClaimDateModal} onOpenChange={setShowClaimDateModal}>
        <DialogContent className="ht-panel text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DollarSign className="ht-pos" size={20} />
              Riscuoti Pagamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Tipo riscossione badge */}
            <div className="flex items-center gap-2 justify-center">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                selectedCycle?.payout_type === '48h' 
                  ? 'bg-[rgba(40,44,52,.5)] text-[#8b919c]' 
                  : 'bg-[rgba(61,219,169,.14)] ht-pos'
              }`}>
                {selectedCycle?.payout_type === '48h' ? 'Riscossione 48h' : 'Riscossione 30gg'}
              </div>
            </div>
            
            {/* Campo importo rimborso ricevuto */}
            <div className="space-y-2">
              <Label className="text-gray-300">Importo Rimborso Ricevuto ($)</Label>
              <Input
                type="number"
                placeholder="Es: 3600"
                value={claimFormData.received_amount}
                onChange={(e) => setClaimFormData({ ...claimFormData, received_amount: e.target.value })}
                className="bg-[#0d0d0d] border-[#282c34] text-white font-mono text-lg"
              />
              <p className="text-xs text-disabled">Inserisci l'importo effettivo ricevuto dalla prop firm</p>
            </div>
            
            {/* Preview calcoli */}
            {claimFormData.received_amount && parseFloat(claimFormData.received_amount) > 0 && (
              <div className="bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)] rounded-lg p-3 space-y-2">
                <p className="text-xs ht-pos">Verrà registrato:</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Rimborso</span>
                    <span className="ht-pos font-mono">{formatCurrency(parseFloat(claimFormData.received_amount))}</span>
                  </div>
                  {selectedCycle?.payout_type !== '48h' && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Profitto (15%)</span>
                      <span className="ht-pos font-mono">{formatCurrency(parseFloat(claimFormData.received_amount) * 0.15)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-[rgba(61,219,169,.4)]">
                    <span className="ht-warn flex items-center gap-1">
                      <Gift size={14} />
                      Credito generato
                    </span>
                    <span className="ht-warn font-mono">{formatCurrency(selectedCycle?.challenge_cost || 0)}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-300">Data</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setClaimFormData({ ...claimFormData, dateOption: 'today' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    claimFormData.dateOption === 'today' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Oggi
                </button>
                <button
                  type="button"
                  onClick={() => setClaimFormData({ ...claimFormData, dateOption: 'custom' })}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                    claimFormData.dateOption === 'custom' 
                      ? 'ht-tab-on' 
                      : 'bg-[#0d0d0d] border-[#282c34] text-gray-400'
                  }`}
                >
                  Seleziona
                </button>
              </div>
              {claimFormData.dateOption === 'custom' && (
                <Input
                  type="date"
                  value={claimFormData.end_date}
                  onChange={(e) => setClaimFormData({ ...claimFormData, end_date: e.target.value })}
                  className="bg-[#0d0d0d] border-[#282c34] text-white mt-2"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDateModal(false)} className="border-[#282c34]">
              Annulla
            </Button>
            <Button 
              onClick={handleClaimPayment} 
              className="ht-btn ht-btn-primary"
              disabled={!claimFormData.received_amount || parseFloat(claimFormData.received_amount) <= 0}
            >
              <DollarSign size={16} className="mr-2" />
              Riscuoti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Credits Modal */}
      <Dialog open={showEditCreditsModal} onOpenChange={setShowEditCreditsModal}>
        <DialogContent className="ht-panel text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Gift className="ht-warn" size={20} />
              Modifica Crediti
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Nuovo valore crediti ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editCreditsValue}
                onChange={(e) => setEditCreditsValue(e.target.value)}
                placeholder="Inserisci nuovo valore"
                className="bg-[#0d0d0d] border-[#282c34] text-white font-mono"
              />
              <p className="text-xs text-disabled">
                Valore attuale: {formatCurrency(credits.total_available)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditCreditsModal(false);
                setEditCreditsValue('');
              }} 
              className="border-[#282c34]"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleEditCredits} 
              className="ht-btn ht-btn-warn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot History Modal */}
      <Dialog open={showSlotHistory} onOpenChange={setShowSlotHistory}>
        <DialogContent className="bg-[#0d1117] border-[#282c34] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="ht-pos" size={20} />
              Storico Slot {selectedSlot?.slotNumber}
              {selectedSlot?.accountLabel && (
                <span className="text-sm text-gray-400 font-normal">({selectedSlot.accountLabel})</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {loadingSlotHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 accent-spinner"></div>
              </div>
            ) : slotHistoryCycles.length === 0 ? (
              <div className="text-center py-8 text-disabled">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun ciclo trovato per questo slot</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Active cycle first (if any) */}
                {(() => {
                  const activeCycle = slotHistoryCycles.find(c => c.status === 'active' || c.status === 'awaiting_payment' || c.status === 'target_pending' || c.status === 'target_wallet');
                  const completedCyclesList = slotHistoryCycles.filter(c => c.status === 'completed');
                  
                  // Group completed cycles by month
                  const groupedByMonth = {};
                  completedCyclesList.forEach(cycle => {
                    const date = cycle.end_date || cycle.start_date;
                    if (!date) return;
                    const monthKey = date.substring(0, 7); // YYYY-MM
                    if (!groupedByMonth[monthKey]) groupedByMonth[monthKey] = [];
                    groupedByMonth[monthKey].push(cycle);
                  });
                  
                  // Sort months descending (most recent first)
                  const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));
                  
                  return (
                    <>
                      {/* Active Cycle Card */}
                      {activeCycle && (
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-2 h-2 bg-[#3ddba9] rounded-full animate-pulse"></span>
                            <span className="text-sm font-medium ht-pos">Ciclo Attivo</span>
                          </div>
                          <div className="bg-gradient-to-br from-usdt/10 to-usdt/5 border border-[rgba(61,219,169,.4)] rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <PropFirmLogo firmName={activeCycle.prop_firm} size={24} />
                                <span className="font-medium text-white">{activeCycle.prop_firm}</span>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                activeCycle.status === 'active' ? 'bg-[rgba(61,219,169,.14)] ht-pos' :
                                activeCycle.status === 'awaiting_payment' ? 'bg-[rgba(139,92,246,.16)] text-[#b9a3f5]' :
                                activeCycle.status === 'target_pending' ? 'bg-[rgba(240,180,90,.14)] ht-warn' :
                                'bg-orange-500/20 text-orange-400'
                              }`}>
                                {activeCycle.status === 'active' ? 'ATTIVO' :
                                 activeCycle.status === 'awaiting_payment' ? 'IN ATTESA' :
                                 activeCycle.status === 'target_pending' ? 'TARGET PENDING' : 'TARGET WALLET'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-disabled">Inizio:</span>
                                <span className="text-white ml-2">{formatDate(activeCycle.start_date)}</span>
                              </div>
                              <div>
                                <span className="text-disabled">Costo:</span>
                                <span className="text-white ml-2 font-mono">{formatCurrency(activeCycle.challenge_cost || 0)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <IdentityAvatar identityName={activeCycle.identity} size={20} identities={settings?.identities} />
                              <span>{activeCycle.identity || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Completed Cycles grouped by month */}
                      {sortedMonths.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle size={16} className="text-gray-400" />
                            <span className="text-sm font-medium text-gray-400">Cicli Completati</span>
                          </div>
                          <div className="space-y-4">
                            {sortedMonths.map(monthKey => {
                              const monthCycles = groupedByMonth[monthKey];
                              return (
                                <div key={monthKey} className="border border-[#282c34] rounded-lg overflow-hidden">
                                  <div className="bg-[#0d0d0d] px-3 py-2 flex items-center justify-between">
                                    <span className="text-sm text-gray-300 font-medium">{getMonthName(monthKey)}</span>
                                    <span className="text-xs text-disabled">{monthCycles.length} cicl{monthCycles.length === 1 ? 'o' : 'i'}</span>
                                  </div>
                                  <div className="divide-y divide-gray-800">
                                    {monthCycles.map(cycle => {
                                      const netProfit = cycle.net_profit || 0;
                                      const isProfittoProp = ['prop_payout', 'prop_payout_30d', 'prop_payout_48h', 'riscosso_anticipato', 'riscosso_anticipato_30d', 'riscosso_anticipato_48h'].includes(cycle.completion_type);
                                      
                                      return (
                                        <div key={cycle.id} className="p-3 hover:bg-[#1a2332] transition-colors">
                                          <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                              <PropFirmLogo firmName={cycle.prop_firm} size={20} />
                                              <span className="text-sm text-white">{cycle.prop_firm}</span>
                                              {isProfittoProp && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(61,219,169,.14)] ht-pos">Prop</span>
                                              )}
                                            </div>
                                            <span className={`text-sm font-mono font-bold ${netProfit >= 0 ? 'ht-pos' : 'text-red-400'}`}>
                                              {formatCurrency(netProfit)}
                                            </span>
                                          </div>
                                          <div className="flex items-center justify-between text-xs text-disabled">
                                            <span>{formatDate(cycle.start_date)} → {formatDate(cycle.end_date)}</span>
                                            <span className={
                                              cycle.completion_type === 'burn' || cycle.completion_type === 'broker_profit' 
                                                ? 'text-red-400' 
                                                : 'text-gray-400'
                                            }>
                                              {cycle.completion_type === 'prop_payout' || cycle.completion_type === 'prop_payout_30d' || cycle.completion_type === 'prop_payout_48h' ? 'Riscosso' :
                                               cycle.completion_type === 'riscosso_anticipato' || cycle.completion_type === 'riscosso_anticipato_30d' || cycle.completion_type === 'riscosso_anticipato_48h' ? 'Anticipo' :
                                               cycle.completion_type === 'burn' || cycle.completion_type === 'broker_profit' ? 'Bruciato' : 'Completato'}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* No cycles message */}
                      {!activeCycle && sortedMonths.length === 0 && (
                        <div className="text-center py-4 text-disabled text-sm">
                          Nessun ciclo trovato per questo slot
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSlotHistory(false)} 
              className="border-[#282c34]"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
